import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { showSuccess, showError } from '@/lib/swal';

export default function EmailChangeVerificationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [message, setMessage] = useState('Verifying your email...');
  const [newEmail, setNewEmail] = useState<string>('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      const userId = searchParams.get('userId');
      const type = searchParams.get('type'); // 'current' or 'new'

      if (!token || !userId || !type) {
        setStatus('error');
        setMessage('Invalid verification link. Please check the link from your email and try again.');
        return;
      }

      try {
        // Determine which endpoint to call based on type
        const isEmulator = import.meta.env.MODE === 'development';
        const baseUrl = isEmulator
          ? 'http://localhost:5001/demo-masonic-tools/europe-west2'
          : 'https://europe-west2-masonic-tools.cloudfunctions.net';

        let endpoint: string;
        if (type === 'current') {
          endpoint = `${baseUrl}/verifyCurrentEmailForChange`;
        } else if (type === 'new') {
          endpoint = `${baseUrl}/verifyNewEmailAndComplete`;
        } else {
          setStatus('error');
          setMessage('Invalid verification type');
          return;
        }

        const response = await fetch(`${endpoint}?token=${token}&userId=${userId}`, {
          method: 'GET',
        });

        const data = await response.json();

        if (data.success) {
          setStatus('success');
          
          if (type === 'current') {
            setMessage('Current email verified! We have sent a verification email to your new email address. Please check your inbox to complete the email change.');
            showSuccess('Please check your new email to complete the verification');
          } else {
            setMessage(`Email successfully changed! You can now log in with your new email address: ${data.newEmail}`);
            setNewEmail(data.newEmail);
            showSuccess('Email changed successfully!');
            
            // Redirect to login after 3 seconds
            setTimeout(() => {
              navigate('/login');
            }, 3000);
          }
        } else {
          setStatus('error');
          setMessage(data.message || 'Verification failed. Please try again or request a new verification email.');
          showError(data.message || 'Verification failed');
        }
      } catch (error: any) {
        console.error('Error verifying email:', error);
        setStatus('error');
        setMessage('An error occurred during verification. Please try again later.');
        showError('Verification error');
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              {status === 'verifying' && (
                <Loader2 className="h-16 w-16 text-primary animate-spin" />
              )}
              {status === 'success' && (
                <CheckCircle className="h-16 w-16 text-green-600" />
              )}
              {status === 'error' && (
                <XCircle className="h-16 w-16 text-red-600" />
              )}
            </div>
            <CardTitle className="text-2xl">
              {status === 'verifying' && 'Verifying Email'}
              {status === 'success' && 'Email Verified'}
              {status === 'error' && 'Verification Failed'}
            </CardTitle>
            <CardDescription>{message}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {status === 'success' && newEmail && (
              <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-900">
                      New Email Address
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      {newEmail}
                    </p>
                    <p className="text-xs text-green-600 mt-2">
                      You will be redirected to the login page in a few seconds...
                    </p>
                  </div>
                </div>
              </div>
            )}

            {status === 'success' && !newEmail && (
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-4">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">
                      Next Step
                    </p>
                    <p className="text-sm text-blue-700 mt-1">
                      Check your new email inbox for the verification link to complete the email change process.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                <p className="text-sm text-red-900">
                  {message}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-2">
              {status === 'success' && !newEmail && (
                <Button onClick={() => navigate('/settings')} className="w-full">
                  Return to Settings
                </Button>
              )}
              {status === 'success' && newEmail && (
                <Button onClick={() => navigate('/login')} className="w-full">
                  Go to Login
                </Button>
              )}
              {status === 'error' && (
                <>
                  <Button onClick={() => navigate('/settings')} className="w-full">
                    Go to Settings
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.location.reload()}
                    className="w-full"
                  >
                    Try Again
                  </Button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

